import React, { useState } from "react";
import { sendEmailOTP, verifyEmailOTP, setPendingNickname } from "../lib/auth";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis as PolarAngleAxisBase,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { ShieldCheck, Wand2 } from "lucide-react";
import { C } from "../constants/design";
import { RARITY_ORD } from "../constants/design";
import { SLOT_DEFS, STATS, BACKGROUND_IMGS, LAYER_IMGS, PP_IMGS, FAMILIAR_PPS, INVENTORY_CAP, coffreCapacity, COFFRE_CHEST_COST_TICKETS, COFFRE_SLOTS_PER_CHEST, MAX_COFFRE_CHESTS } from "../constants/items";
import { StatIcon } from "./ui/StatIcon";
import { PVE_SPELLS, SPELL_POOL } from "../features/stadium-trial/content/pveSpells";
import { FONT } from "../features/stadium-trial/components/StadiumTrialScreen";
import { M_BY_ID } from "../constants/matches";
import { iPower, iName, iSkin, baseVitality } from "../engine/itemEngine";
import { getEquipmentEnergyBonus } from "../features/league-duel/engine/combat";
import { levelProgress, formatScore } from "../engine/playerEngine";
import { Fl, SH } from "./ui/atoms";
import { ItemToken } from "./ui/ItemCard";
import { ItemModal } from "./ui/ItemModal";
import { ItemRedesignModal } from "./ui/ItemRedesignModal";
import type { Item, Player } from "../types/game";
import { useI18n } from "../i18n/I18nContext";
import type { Language } from "../i18n/I18nContext";

const PolarAngleAxis = PolarAngleAxisBase as unknown as React.FC<any>;

// ─── Réglages de l'avatar ────────────────────────────────────
// Modifie AVATAR_WIDTH pour redimensionner le personnage — tous les calques suivent.
const AVATAR_WIDTH = 305;
const AVATAR_OFFSET_Y = -18; // décalage vertical en px (négatif = monte)
const AVATAR_OFFSET_X = -15; // décalage horizontal en px (négatif = gauche)

const CHARACTER_IMG = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/Character.png";
const TICKET_ICON = "https://i.postimg.cc/d3sX5KSp/ticket-foot-viol-(1).png";

/** Une case d'équipement : l'item équipé, sinon un emplacement vide. */
function EquipSlot({
  slot,
  it,
  onClick,
}: {
  slot: (typeof SLOT_DEFS)[number];
  it: Item | null;
  onClick: () => void;
}) {
  if (it) return <ItemToken it={it} onClick={onClick} />;
  return (
    <div
      style={{
        aspectRatio: "1",
        borderRadius: 11,
        border: `1.5px dashed rgba(255,255,255,0.30)`,
        background: "rgba(255,255,255,0.05)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        opacity: 0.85,
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1 }}>{slot.emoji}</span>
    </div>
  );
}

const pickPts = (m: { p1: number; pX: number; p2: number }, pick: string) => {
  const p = pick === "team1" ? m.p1 : pick === "draw" ? m.pX : m.p2;
  return Math.max(5, Math.round(100 / p / 5) * 5);
};

const getRank = (power: number) => {
  if (power >= 160)
    return {
      label: "LÉGENDAIRE",
      accent: "#F5B301",
      dim: "#7a5900",
      strip: "linear-gradient(90deg,#F5B301,#c98f00,#F5B301)",
    };
  if (power >= 110)
    return {
      label: "ÉLITE",
      accent: "#A855F7",
      dim: "#5b1fa8",
      strip: "linear-gradient(90deg,#A855F7,#7c1fe8)",
    };
  if (power >= 65)
    return {
      label: "EXPERT",
      accent: "#3B82F6",
      dim: "#1a4fb5",
      strip: "linear-gradient(90deg,#3B82F6,#1a4fb5)",
    };
  if (power >= 30)
    return {
      label: "PRO",
      accent: "#2DE36B",
      dim: "#0d6b31",
      strip: "linear-gradient(90deg,#2DE36B,#159B52)",
    };
  return {
    label: "ROOKIE",
    accent: "#8696B0",
    dim: "#3d4f68",
    strip: "linear-gradient(90deg,#8696B0,#5C6B86)",
  };
};

export function Profil({
  me,
  derived,
  bets,
  results,
  onEquip,
  onUnequip,
  onAuto,
  savePlayer,
  onSell,
  onStore,
  onRetrieve,
  onBuyCoffre,
  onTestLevelUp,
  onTestBetResult,
  onSignOut,
  isGuest,
  onLinkAccount,
  onOpenDuel,
}: {
  me: Player;
  derived: {
    power: number;
    earned: number;
    won: number;
    total: number;
    winrate: number;
    wallet: number;
    stats: Record<string, number>;
  };
  bets: Record<string, string>;
  results: Record<string, { o: string; s?: string }>;
  onEquip: (it: Item) => void;
  onUnequip: (it: Item) => void;
  onAuto?: () => void;
  savePlayer?: (p: Player) => void;
  onSell: (it: Item) => void;
  onStore?: (it: Item) => void;
  onRetrieve?: (it: Item) => void;
  onBuyCoffre?: () => void;
  onTestLevelUp?: () => void;
  onTestBetResult?: () => void;
  onSignOut?: () => void;
  isGuest?: boolean;
  onLinkAccount?: (provider: "google") => void;
  onOpenDuel?: () => void;
}) {
  const { t: tr, language, setLanguage } = useI18n();
  const [tab, setTab] = React.useState("equipment");
  const [selectedItem, setSelectedItem] = React.useState<Item | null>(null);
  const [coffreOpen, setCoffreOpen] = React.useState(false);
  const [redesignOpen, setRedesignOpen] = React.useState(false);
  const [vaultTab, setVaultTab] = React.useState(0); // onglet actif (index 0-based)
  const [confirmExtend, setConfirmExtend] = React.useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveEmailMode, setSaveEmailMode] = useState(false);
  const [saveEmail, setSaveEmail] = useState("");
  const [saveEmailError, setSaveEmailError] = useState<string | null>(null);
  const [saveCodeSent, setSaveCodeSent] = useState(false);
  const [saveCode, setSaveCode] = useState("");
  const [saveCodeError, setSaveCodeError] = useState<string | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);

  const resetSaveModal = () => {
    setSaveModalOpen(false);
    setSaveEmailMode(false);
    setSaveEmail("");
    setSaveEmailError(null);
    setSaveCodeSent(false);
    setSaveCode("");
    setSaveCodeError(null);
    setSaveBusy(false);
  };

  const handleSaveSendCode = async () => {
    const v = saveEmail.trim();
    if (!v || !v.includes("@")) { setSaveEmailError(tr("login.invalidEmail")); return; }
    setSaveBusy(true);
    setPendingNickname(me.name);
    const ok = await sendEmailOTP(v);
    setSaveBusy(false);
    if (ok) { setSaveCodeSent(true); setSaveEmailError(null); }
    else { setSaveEmailError(tr("login.sendFailed")); }
  };

  const handleSaveVerifyCode = async () => {
    const t = saveCode.trim();
    if (t.length < 4) { setSaveCodeError(tr("login.enterCodeFromEmail")); return; }
    setSaveBusy(true);
    const session = await verifyEmailOTP(saveEmail.trim(), t);
    setSaveBusy(false);
    if (session) { window.location.reload(); }
    else { setSaveCodeError(tr("login.invalidCode")); }
  };

  const inv = me.inventory || [];
  const equipped = me.equipped || {};
  const equippedBySlot: Record<string, Item | null> = {};
  SLOT_DEFS.forEach((slot) => {
    const iid = equipped[slot.key];
    equippedBySlot[slot.key] = iid
      ? inv.find((x) => x.iid === iid) || null
      : null;
  });
  const equippedIids = new Set(Object.values(equipped).filter(Boolean));
  const unequippedItems = [...inv]
    .filter((it) => !equippedIids.has(it.iid))
    .sort(
      (a, b) =>
        RARITY_ORD.indexOf(a.rarity) - RARITY_ORD.indexOf(b.rarity) ||
        iPower(b) - iPower(a)
    );
  const s = derived.stats;
  const inj = Math.min(75, s.blessure || 0);
  const radarData = STATS.map((st) => ({ stat: st.short, value: s[st.key] || 0 }));
  const maxV = Math.max(20, ...STATS.map((st) => s[st.key] || 0));
  const recent = Object.entries(bets || {})
    .map(([mid, pick]) => ({ m: M_BY_ID[mid], pick, res: results[mid] }))
    .filter((x) => x.m && x.res)
    .slice(-6)
    .reverse();
  const isEquipped = selectedItem
    ? equipped[selectedItem.slot] === selectedItem.iid
    : false;
  const rank = getRank(derived.power);
  const xp = levelProgress(derived.earned + (me.duel_xp ?? 0));
  // Décomposition de la Vie : socle donné par le niveau vs apport du stuff.
  const baseVit = baseVitality(xp.level);
  const gearVit = Math.max(0, (s.vitesse || 0) - baseVit);
  // Gain d'énergie par auto-attaque (rotation) = base 10 + affixe(s) ballon
  // équipé (chaque point d'affixe = +1 point de jauge = +10%).
  const energyAffix = getEquipmentEnergyBonus(inv, equipped);
  const energyGainPerAuto = 10 + energyAffix;
  const isTester = me.name === "aseo.bonjour";
  const equippedBgItem = equippedBySlot["background"];
  const backgroundImg = equippedBgItem ? BACKGROUND_IMGS[iSkin(equippedBgItem)] : null;
  // Calques par slot (null si rien d'équipé ou pas d'image)
  const layerImg = (slot: string) => {
    const it = equippedBySlot[slot];
    return it ? (LAYER_IMGS[iSkin(it)] ?? null) : null;
  };
  // Couleur de profil unifiée (verte) — plus de coloration selon le rang/aura.
  const accent = "#2DE36B";
  const strip = "linear-gradient(90deg,#2DE36B,#159B52)";

  const handleRedesign = (sourceIid: string, targetIid: string) => {
    const source = inv.find((i) => i.iid === sourceIid);
    const target = inv.find((i) => i.iid === targetIid);
    if (!source || !target || source.slot !== target.slot) return;
    if ((me.tickets ?? 0) < 1) return;
    const newInventory = inv
      .filter((i) => i.iid !== targetIid)
      .map((i) => i.iid === sourceIid ? { ...i, cosmetic: target.name } : i);
    savePlayer?.({ ...me, inventory: newInventory, tickets: (me.tickets ?? 0) - 1 });
  };

  return (
    <div>
      {/* ── Settings modal ──────────────────────────────────────────── */}
      {settingsOpen && (
        <div
          onClick={() => { setSettingsOpen(false); setConfirmSignOut(false); }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.surface,
              borderRadius: 22,
              border: "1.5px solid rgba(255,255,255,0.08)",
              width: "100%",
              maxWidth: 360,
              padding: 24,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 900 }}>{tr("profil.settingsTitle")}</div>

            {/* Info joueur */}
            <div style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
              <span style={{ fontSize: 24 }}>👤</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{me.name}</div>
                <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>
                  {isGuest ? tr("profil.guestAccount") : tr("profil.linkedAccount")}
                </div>
              </div>
            </div>

            {/* Language selector */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              padding: "12px 14px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>Language</span>
              <div style={{ display: "flex", gap: 6 }}>
                {(["en", "ko", "fr"] as Language[]).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setLanguage(lang)}
                    style={{
                      flex: 1,
                      border: language === lang ? "1.5px solid #48FF85" : "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 8,
                      padding: "7px 8px",
                      fontSize: 12,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      background: language === lang ? "rgba(72,255,133,0.12)" : "transparent",
                      color: language === lang ? "#48FF85" : C.muted,
                      cursor: "pointer",
                    }}
                  >
                    {lang === "en" ? "English" : lang === "ko" ? "한국어" : "Français"}
                  </button>
                ))}
              </div>
            </div>

            {/* Sign out */}
            {onSignOut && (
              confirmSignOut ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 14, padding: "14px 16px" }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#f87171" }}>{tr("profil.signOutConfirmTitle")}</div>
                  <div style={{ fontSize: 12, color: "#f87171", opacity: 0.8 }}>{tr("profil.signOutConfirmSub")}</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => setConfirmSignOut(false)}
                      style={{
                        flex: 1, border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "11px",
                        background: "transparent", color: "#8696B0", fontWeight: 700, fontSize: 13, cursor: "pointer",
                      }}
                    >
                      {tr("profil.cancel")}
                    </button>
                    <button
                      onClick={onSignOut}
                      style={{
                        flex: 1, border: "none", borderRadius: 10, padding: "11px",
                        background: "#ef4444", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer",
                      }}
                    >
                      {tr("profil.signOutConfirmBtn")}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={isGuest ? () => setConfirmSignOut(true) : onSignOut}
                  style={{
                    border: "1px solid rgba(239,68,68,0.35)",
                    borderRadius: 12,
                    padding: "13px",
                    fontWeight: 800,
                    fontSize: 14,
                    background: "rgba(239,68,68,0.08)",
                    color: "#f87171",
                    cursor: "pointer",
                    marginTop: isGuest ? 0 : 4,
                  }}
                >
                  {tr("profil.signOut")}
                </button>
              )
            )}

            <button
              onClick={() => { setSettingsOpen(false); setConfirmSignOut(false); }}
              style={{
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 12, padding: "11px",
                background: "transparent", color: C.muted,
                fontWeight: 700, fontSize: 14, cursor: "pointer",
              }}
            >
              {tr("profil.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* ── Save progress modal ─────────────────────────────────────── */}
      {saveModalOpen && (
        <div
          onClick={resetSaveModal}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.surface, borderRadius: 22,
              border: "1.5px solid rgba(255,255,255,0.08)",
              width: "100%", maxWidth: 360,
              padding: 24,
              display: "flex", flexDirection: "column", gap: 14,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 900 }}>{tr("profil.saveModalTitle")}</div>
            <div style={{ fontSize: 13, color: C.muted }}>
              {tr("profil.saveModalSubtitle")}
            </div>

            {!saveEmailMode ? (
              <>
                {onLinkAccount && (
                  <button
                    onClick={() => onLinkAccount("google")}
                    style={{
                      border: "none", borderRadius: 12, height: 50,
                      padding: "0 14px", fontWeight: 600, fontSize: 15,
                      background: "#fff", color: "#1f1f1f",
                      cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "center", gap: 10, width: "100%",
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {tr("profil.withGoogle")}
                  </button>
                )}
                <button
                  onClick={() => setSaveEmailMode(true)}
                  style={{
                    border: "none", borderRadius: 12, height: 50,
                    padding: "0 14px", fontWeight: 600, fontSize: 15,
                    background: "rgba(255,255,255,0.18)", color: "#fff",
                    cursor: "pointer", display: "flex",
                    alignItems: "center", justifyContent: "center", gap: 10, width: "100%",
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                  {tr("profil.continueWithEmail")}
                </button>
              </>
            ) : (
              <>
                <input
                  type="email"
                  autoFocus
                  value={saveEmail}
                  onChange={(e) => { setSaveEmail(e.target.value); setSaveEmailError(null); }}
                  onKeyDown={(e) => e.key === "Enter" && !saveCodeSent && handleSaveSendCode()}
                  placeholder={tr("login.emailPlaceholder")}
                  style={{
                    background: C.bg2,
                    border: `1px solid ${saveEmailError ? "#ef4444" : "rgba(255,255,255,0.12)"}`,
                    borderRadius: 12, padding: "13px 14px",
                    color: C.text, fontSize: 15, outline: "none",
                  }}
                />
                {saveEmailError && (
                  <div style={{ color: "#ef4444", fontSize: 12, fontWeight: 600 }}>{saveEmailError}</div>
                )}
                {!saveCodeSent ? (
                  <button
                    disabled={saveBusy}
                    onClick={handleSaveSendCode}
                    style={{
                      border: "none", borderRadius: 12, padding: "13px",
                      fontWeight: 700, fontSize: 14,
                      background: "#48FF85", color: "#06121e",
                      cursor: saveBusy ? "default" : "pointer",
                      opacity: saveBusy ? 0.6 : 1,
                    }}
                  >
                    {saveBusy ? tr("login.sending") : tr("login.receiveCode")}
                  </button>
                ) : (
                  <>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                      {tr("login.codeSentTo", saveEmail)}
                    </div>
                    <input
                      autoFocus
                      value={saveCode}
                      maxLength={8}
                      inputMode="numeric"
                      onChange={(e) => { setSaveCode(e.target.value); setSaveCodeError(null); }}
                      onKeyDown={(e) => e.key === "Enter" && handleSaveVerifyCode()}
                      placeholder={tr("login.enterCodePlaceholder")}
                      style={{
                        background: C.bg2,
                        border: `1px solid ${saveCodeError ? "#ef4444" : "rgba(255,255,255,0.12)"}`,
                        borderRadius: 12, padding: "13px 14px",
                        color: C.text, fontSize: 20, outline: "none",
                        letterSpacing: 6, textAlign: "center" as const,
                      }}
                    />
                    {saveCodeError && (
                      <div style={{ color: "#ef4444", fontSize: 12, fontWeight: 600 }}>{saveCodeError}</div>
                    )}
                    <button
                      disabled={saveBusy}
                      onClick={handleSaveVerifyCode}
                      style={{
                        border: "none", borderRadius: 12, padding: "13px",
                        fontWeight: 700, fontSize: 14,
                        background: "#48FF85", color: "#06121e",
                        cursor: saveBusy ? "default" : "pointer",
                        opacity: saveBusy ? 0.6 : 1,
                      }}
                    >
                      {saveBusy ? tr("login.verifying") : tr("login.signIn")}
                    </button>
                  </>
                )}
                <button
                  onClick={() => { setSaveEmailMode(false); setSaveCodeSent(false); setSaveCode(""); setSaveEmail(""); setSaveEmailError(null); setSaveCodeError(null); }}
                  style={{
                    background: "transparent", border: "none",
                    color: C.faint, fontSize: 13, cursor: "pointer", padding: "4px 0",
                  }}
                >
                  {tr("login.back")}
                </button>
              </>
            )}

            <button
              onClick={resetSaveModal}
              style={{
                border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "11px",
                background: "transparent", color: C.muted, fontWeight: 700, fontSize: 14, cursor: "pointer",
              }}
            >
              {tr("profil.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* ── Save progress banner ─────────────────────────────────────── */}
      {isGuest && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "linear-gradient(90deg,rgba(245,179,1,0.12),rgba(245,179,1,0.06))",
          border: "1px solid rgba(245,179,1,0.3)",
          borderRadius: 14, padding: "12px 16px", marginBottom: 14, gap: 12,
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: "#F5B301" }}>{tr("profil.saveProgressTitle")}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              {tr("profil.saveProgressSubtitle")}
            </div>
          </div>
          <button
            onClick={() => setSaveModalOpen(true)}
            style={{
              border: "none", borderRadius: 10, padding: "9px 16px",
              fontWeight: 700, fontSize: 13,
              background: "#F5B301", color: "#06121e",
              cursor: "pointer", flexShrink: 0,
            }}
          >
            {tr("profil.save")}
          </button>
        </div>
      )}

      {/* ── CARTE JOUEUR ─────────────────────────────────────────────── */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(160deg,${C.surface2},${C.bg2})`,
          border: `1px solid ${accent}44`,
          borderRadius: 18,
          marginBottom: 14,
        }}
      >
        <div style={{ height: 3, background: strip }} />

        <div style={{ padding: "14px 16px 16px" }}>
          {/* Bouton settings — angle top-right de la carte */}
          <button
            onClick={() => setSettingsOpen(true)}
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              display: "grid",
              placeItems: "center",
              fontSize: 19,
              cursor: "pointer",
              color: C.muted,
              zIndex: 2,
            }}
          >
            ⚙
          </button>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 14,
            }}
          >
            <div
              onClick={() => setShowAvatarPicker((v) => !v)}
              title="Change avatar"
              style={{ position: "relative", flexShrink: 0, cursor: "pointer" }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: C.bg2,
                  overflow: "hidden",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                {me.avatar && (PP_IMGS[me.avatar] ?? LAYER_IMGS[me.avatar]) ? (
                  <img
                    src={PP_IMGS[me.avatar] ?? LAYER_IMGS[me.avatar]}
                    alt="Avatar"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span style={{ fontSize: 22 }}>👤</span>
                )}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="d"
                style={{ fontWeight: 900, fontSize: 20, letterSpacing: -0.3 }}
              >
                {me.name}
              </div>
              {showAvatarPicker && (
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>

                {/* PP exclusives débloquées par familier */}
                {([] as string[]).concat(
                  ...Object.entries(FAMILIAR_PPS)
                    .filter(([famName]) =>
                      me.inventory?.some((it) => it.slot === "familiar" && iName(it) === famName)
                    )
                    .map(([, ppKeys]: [string, string[]]) => ppKeys)
                )
                  .map((ppKey: string) => {
                    const isSelected = me.avatar === ppKey;
                    return (
                      <button
                        key={ppKey}
                        onClick={() => {
                          if (typeof savePlayer === "function")
                            savePlayer({ ...me, avatar: ppKey });
                        }}
                        title={ppKey}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: isSelected ? `${C.turf}22` : C.surface,
                          border: `2px solid ${isSelected ? C.turf : C.line}`,
                          cursor: "pointer",
                          overflow: "hidden",
                          padding: 0,
                          transition: "border-color .15s",
                        }}
                      >
                        <img src={PP_IMGS[ppKey]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </button>
                    );
                  })}
              </div>
              )}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gap: 6,
            }}
          >
            {[
              { v: derived.earned.toLocaleString("en-US"), l: tr("profil.statPoints"), c: C.turf },
              { v: `${Math.round(derived.winrate * 100)}%`, l: tr("profil.statWinrate"), c: null },
              { v: `${derived.won}/${derived.total}`, l: tr("profil.statPicks"), c: null },
              { v: String(derived.power), l: tr("profil.statPower"), c: C.gold },
            ].map(({ v, l, c }) => (
              <div
                key={l}
                style={{
                  background: C.bg,
                  borderRadius: 10,
                  padding: "9px 6px",
                  textAlign: "center",
                  border: `1px solid ${C.lineSoft}`,
                }}
              >
                <div
                  style={{
                    fontFamily: "monospace",
                    fontWeight: 900,
                    fontSize: 16,
                    color: c || C.text,
                  }}
                >
                  {v}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    color: C.faint,
                    fontWeight: 800,
                    marginTop: 2,
                    letterSpacing: 1,
                  }}
                >
                  {l}
                </div>
              </div>
            ))}
          </div>

          {/* ── Barre d'XP / Niveau (compacte) ───────────────────────── */}
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 5, flexShrink: 0 }}>
              <span
                style={{
                  fontSize: 8.5,
                  fontWeight: 800,
                  color: C.faint,
                  letterSpacing: 1.5,
                }}
              >
                {tr("profil.levelLabel")}
              </span>
              <span
                style={{
                  fontFamily: "monospace",
                  fontWeight: 900,
                  fontSize: 18,
                  lineHeight: 1,
                  color: accent,
                }}
              >
                {xp.level}
              </span>
            </div>
            <div
              style={{
                flex: 1,
                height: 6,
                borderRadius: 6,
                background: C.bg,
                border: `1px solid ${C.lineSoft}`,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.round(xp.ratio * 100)}%`,
                  height: "100%",
                  background: strip,
                  transition: "width .4s ease",
                }}
              />
            </div>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: C.muted,
                flexShrink: 0,
              }}
            >
              {formatScore(xp.into)}/{formatScore(xp.span)}
            </span>
          </div>
        </div>
      </div>

      {/* ── Tab switcher ─────────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 4,
          marginBottom: 14,
          background: C.bg2,
          border: `1px solid ${C.line}`,
          borderRadius: 12,
          padding: 4,
        }}
      >
        {[
          { key: "equipment", label: tr("profil.equipmentTab") },
          { key: "spells", label: "✨ Spells" },
          { key: "stats", label: tr("profil.statisticsTab") },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "9px 0",
              borderRadius: 9,
              border: "none",
              fontWeight: 800,
              fontSize: 12.5,
              cursor: "pointer",
              background: tab === t.key ? accent : "transparent",
              color: tab === t.key ? "#040a07" : C.muted,
              transition: "background .15s, color .15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ONGLET ÉQUIPEMENT ─────────────────────────────────────────── */}
      <div style={{ display: tab === "equipment" ? undefined : "none" }}>
        <div>
          {/* Wrapper : l'image dicte la hauteur du bloc, le grid s'overlay par-dessus */}
          <div style={{ position: "relative", marginBottom: 18 }}>
            {backgroundImg && (
              <img
                src={backgroundImg}
                alt=""
                style={{
                  display: "block",
                  width: "calc(100% + 28px)",  // déborde 14px de chaque côté = bords écran
                  marginLeft: -14,
                  height: "auto",              // hauteur naturelle = pas de crop
                  pointerEvents: "none",
                }}
              />
            )}
          <div
            style={{
              position: backgroundImg ? "absolute" : "relative",
              inset: 0,
              display: "grid",
              gridTemplateColumns: "76px 1fr 76px",
              gap: 8,
              alignItems: "center",
              padding: "4px 2px",
            }}
          >
            {/* Dégradé radial quand pas de fond */}
            {!backgroundImg && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: `radial-gradient(circle at 50% 40%, ${C.turf}1F, ${C.surface}26 38%, transparent 66%)`,
                pointerEvents: "none",
                borderRadius: 22,
              }}
            />
            )}

            {/* Colonne gauche : slots z:3 → toujours au-dessus du personnage */}
            <div
              style={{
                position: "relative",
                zIndex: 3,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {SLOT_DEFS.slice(0, 3).map((slot) => (
                <EquipSlot
                  key={slot.key}
                  slot={slot}
                  it={equippedBySlot[slot.key]}
                  onClick={() => {
                    const it = equippedBySlot[slot.key];
                    if (it) setSelectedItem(it);
                  }}
                />
              ))}
            </div>

            {/* Personnage + calques d'équipement — z:1 sous les slots, minWidth:0 anti-overflow */}
            <div
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                alignSelf: "stretch",
                minWidth: 0,
              }}
            >
              {/* Wrapper : Character.png dicte la taille — tous les calques s'y accrochent */}
              <div
                style={{
                  position: "relative",
                  width: AVATAR_WIDTH,
                  marginTop: AVATAR_OFFSET_Y,
                  marginLeft: AVATAR_OFFSET_X,
                }}
              >
                {/* Image fantôme qui impose la hauteur du container */}
                <img src={CHARACTER_IMG} alt="" style={{ display: "block", width: "100%", height: "auto", visibility: "hidden" }} />

                {/* Calque Ball (z 0 — dernier plan) */}
                {layerImg("ball") && (
                  <img src={layerImg("ball")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 0, pointerEvents: "none" }} />
                )}
                {/* Calque Familiar (z 1) */}
                {layerImg("familiar") && (
                  <img src={layerImg("familiar")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 1, pointerEvents: "none" }} />
                )}
                {/* Calque Character (z 2 — base toujours visible) */}
                <img src={CHARACTER_IMG} alt="Character" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 2, pointerEvents: "none" }} />
                {/* Calque Shoes (z 3) */}
                {layerImg("shoes") && (
                  <img src={layerImg("shoes")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 3, pointerEvents: "none" }} />
                )}
                {/* Calque Top / Kit (z 4) */}
                {layerImg("top") && (
                  <img src={layerImg("top")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 4, pointerEvents: "none" }} />
                )}
                {/* Calque Head (z 5 — premier plan) */}
                {layerImg("head") && (
                  <img src={layerImg("head")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 5, pointerEvents: "none" }} />
                )}
              </div>
            </div>

            {/* Colonne droite : slots z:3 → toujours au-dessus du personnage */}
            <div
              style={{
                position: "relative",
                zIndex: 3,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {SLOT_DEFS.slice(3, 6).map((slot) => (
                <EquipSlot
                  key={slot.key}
                  slot={slot}
                  it={equippedBySlot[slot.key]}
                  onClick={() => {
                    const it = equippedBySlot[slot.key];
                    if (it) setSelectedItem(it);
                  }}
                />
              ))}
            </div>
          </div>
          </div>

          {/* Barre de stats — résumé des 4 stats d'équipement */}
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              background: "#0A0F18",
              border: `1px solid ${C.line}`,
              borderRadius: 16,
              overflow: "hidden",
              marginBottom: 18,
            }}
          >
            {STATS.map((st, i) => {
              return (
                <div
                  key={st.key}
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 9,
                    padding: "14px 0",
                    borderLeft: i === 0 ? "none" : `1px solid ${C.line}`,
                  }}
                >
                  <StatIcon st={st} size={26} />
                  <span style={{ fontWeight: 900, fontSize: 17, color: C.text }}>
                    {s[st.key] || 0}
                  </span>
                </div>
              );
            })}
          </div>

          {/* En-tête inventaire : titre + pastille du nombre, bouton auto-equip à droite */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "4px 2px 11px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h2 className="d" style={{ fontWeight: 900, fontSize: 18, margin: 0, letterSpacing: -0.2 }}>
                {tr("profil.inventoryTitle")}
              </h2>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: unequippedItems.length >= INVENTORY_CAP ? "#f38ba8" : C.muted,
                  background: unequippedItems.length >= INVENTORY_CAP ? "rgba(243,139,168,0.14)" : "rgba(255,255,255,0.06)",
                  border: `1px solid ${unequippedItems.length >= INVENTORY_CAP ? "#f38ba8" : C.line}`,
                  borderRadius: 999,
                  padding: "1px 8px",
                  lineHeight: 1.6,
                }}
                title="Inventory capacity — equipped items don't count"
              >
                {unequippedItems.length} / {INVENTORY_CAP}
              </span>
              {(me.pending_items?.length ?? 0) > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#1e1e2e",
                    background: "#f9e2af",
                    borderRadius: 999,
                    padding: "1px 8px",
                    lineHeight: 1.6,
                  }}
                >
                  📦 {me.pending_items!.length} waiting
                </span>
              )}
            </div>
            {onAuto && unequippedItems.length > 0 && (
              <button
                onClick={onAuto}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 11.5,
                  fontWeight: 800,
                  color: C.turf,
                  background: `${C.turf}14`,
                  border: `1px solid ${C.turf}44`,
                  borderRadius: 999,
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                <Wand2 size={13} /> {tr("profil.autoEquip")}
              </button>
            )}
          </div>

          {/* Inventory grid — items non équipés + placeholders pour les slots
              libres. Les équipés ne comptent PAS dans le cap, donc on affiche au
              moins CAP cases (plus si l'overflow non équipé dépasse CAP). */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 8,
              marginBottom: 10,
            }}
          >
            {Array.from({
              length: Math.max(unequippedItems.length, INVENTORY_CAP),
            }).map((_, idx) => {
              const it = unequippedItems[idx];
              if (it) {
                const eq = equippedBySlot[it.slot];
                const isUpgrade = iPower(it) > (eq ? iPower(eq) : 0);
                return (
                  <ItemToken
                    key={it.iid}
                    it={it}
                    isUpgrade={isUpgrade}
                    onClick={() => setSelectedItem(it)}
                  />
                );
              }
              return (
                <div
                  key={`empty-${idx}`}
                  style={{
                    aspectRatio: "1",
                    borderRadius: 12,
                    border: `1.5px dashed rgba(255,255,255,0.12)`,
                    background: "rgba(255,255,255,0.02)",
                  }}
                />
              );
            })}
          </div>

          {/* Bottom action buttons */}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setRedesignOpen(true)}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "11px 0",
                borderRadius: 12,
                border: "1px solid rgba(212,175,55,0.35)",
                background: "rgba(212,175,55,0.08)",
                color: "#D4AF37",
                fontSize: 13,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              ✨ Item Redesign
            </button>
            {onRetrieve && (
              <button
                onClick={() => { setVaultTab(0); setConfirmExtend(false); setCoffreOpen(true); }}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "11px 0",
                  borderRadius: 12,
                  border: "1px solid rgba(249,226,175,0.3)",
                  background: "rgba(249,226,175,0.07)",
                  color: "#f9e2af",
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                📦 Vault
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── ONGLET SORTS — catalogue avec déblocage par niveau ─────────── */}
      <div style={{ display: tab === "spells" ? undefined : "none" }}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
          Spells unlock as you level up. Equip them in Duel (attack & defense rotations) and Stadium Trial.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {SPELL_POOL.map((id) => {
            const def = PVE_SPELLS[id];
            const isUnlocked = xp.level >= def.unlockLevel;
            return (
              <div key={id} style={{
                display: "flex", gap: 12, alignItems: "flex-start", position: "relative",
                background: isUnlocked ? `linear-gradient(180deg,${def.color}16,${def.color}05)` : "rgba(255,255,255,0.03)",
                borderRadius: 13, padding: "11px 12px",
                opacity: isUnlocked ? 1 : 0.5,
              }}>
                {/* Icon cube + teardrop mana badge — same token as the Duel rotation slots */}
                <div style={{
                  flexShrink: 0, position: "relative", width: 44, height: 44, borderRadius: 12,
                  display: "grid", placeItems: "center", fontSize: 24,
                  background: isUnlocked ? `${def.color}1c` : "rgba(255,255,255,0.04)",
                  filter: isUnlocked ? undefined : "grayscale(1)",
                }}>
                  {def.icon}
                  <span style={{
                    position: "absolute", bottom: -4, right: -4, width: 20, height: 20,
                    background: "#4CC9F0", borderRadius: "0 50% 50% 50%", transform: "rotate(45deg)",
                    display: "grid", placeItems: "center",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.45)", border: "1.5px solid rgba(8,12,20,0.9)",
                  }}>
                    <span style={{ transform: "rotate(-45deg)", font: `700 10px ${FONT.display}`, color: "#0c1322" }}>{def.cost * 10}</span>
                  </span>
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ font: `700 14px ${FONT.display}`, letterSpacing: 0.5, color: isUnlocked ? def.color : C.muted }}>{def.name}</span>
                    <span style={{ marginLeft: "auto", font: `700 10px ${FONT.label}`, letterSpacing: 1, textTransform: "uppercase", color: isUnlocked ? "#2DE36B" : C.muted, whiteSpace: "nowrap" }}>
                      {isUnlocked ? "✓ Unlocked" : `🔒 Level ${def.unlockLevel}`}
                    </span>
                  </div>
                  <div style={{ font: `500 11.5px ${FONT.ui}`, color: "#9fb0c8", lineHeight: 1.35, marginTop: 3 }}>{def.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── ONGLET STATISTIQUES ──────────────────────────────────────── */}
      <div style={{ display: tab === "stats" ? undefined : "none" }}>
        <div>
          <SH title={tr("profil.equipmentStatsTitle")} sub={tr("profil.equipmentStatsSub")} />
          <div
            style={{
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 14,
              padding: "14px 14px 10px",
              marginBottom: 12,
            }}
          >
            {STATS.map((st) => {
              const val = s[st.key] || 0;
              const isLife = st.key === "vitesse";
              return (
                <div key={st.key} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <StatIcon st={st} size={22} />
                    <span style={{ fontSize: 12, color: C.muted }}>
                      {tr(`items.stats.${st.key}`)}
                    </span>
                    {isLife && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: C.faint, whiteSpace: "nowrap" }}>
                        {gearVit} {tr("profil.statFromGear")} · {baseVit} {tr("profil.statFromLevel")}
                      </span>
                    )}
                    <span
                      style={{
                        marginLeft: "auto",
                        fontFamily: "monospace",
                        fontWeight: 900,
                        fontSize: 14,
                        color: val > 0 ? C.text : C.faint,
                        minWidth: 28,
                        textAlign: "right",
                      }}
                    >
                      {val}
                    </span>
                  </div>
                </div>
              );
            })}
            {/* Energy gain per auto attack (rotation) — separate from the 4 primary stats */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.line}` }}>
              <span style={{ width: 22, textAlign: "center", fontSize: 17 }}>💧</span>
              <span style={{ fontSize: 12, color: C.muted, flex: 1 }}>
                Energy gain
              </span>
              <span style={{ fontFamily: "monospace", fontWeight: 900, fontSize: 14, color: C.text, textAlign: "right" }}>
                +{energyGainPerAuto}
                <span style={{ color: energyAffix > 0 ? "#2DE36B" : C.muted, fontWeight: 800, fontSize: 12 }}> (+{energyAffix * 10}%)</span>
              </span>
            </div>
          </div>

          <div
            style={{
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 14,
              padding: "8px 4px 4px",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: C.faint,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: "uppercase",
                textAlign: "center",
                padding: "4px 0 0",
              }}
            >
              {tr("profil.radarLabel")}
            </div>
            <div style={{ height: 190 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="70%">
                  <PolarGrid stroke={C.lineSoft} />
                  <PolarAngleAxis
                    dataKey="stat"
                    tick={{ fill: C.muted, fontSize: 11, fontWeight: 700 }}
                  />
                  <PolarRadiusAxis
                    domain={[0, maxV]}
                    tick={false}
                    axisLine={false}
                  />
                  <Radar
                    dataKey="value"
                    stroke={accent}
                    fill={accent}
                    fillOpacity={0.25}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div
            style={{
              background: `linear-gradient(135deg,#2a1420,${C.surface})`,
              border: "1px solid #5a2a3c",
              borderRadius: 14,
              padding: "13px 14px",
              marginBottom: 14,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <ShieldCheck size={16} color="#e06a8c" />
              <span style={{ fontSize: 13, fontWeight: 800, flex: 1 }}>
                {tr("profil.injuryReduction")}
              </span>
              <span
                style={{
                  fontFamily: "monospace",
                  fontWeight: 900,
                  fontSize: 18,
                  color: "#e06a8c",
                }}
              >
                {inj}%
              </span>
            </div>
            <div
              style={{
                height: 6,
                background: C.bg2,
                borderRadius: 999,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${(inj / 75) * 100}%`,
                  height: "100%",
                  background: "linear-gradient(90deg,#e06a8c,#f5b301)",
                  borderRadius: 999,
                }}
              />
            </div>
          </div>

          {isTester && onTestLevelUp && onTestBetResult && (
            <div style={{
              background: "rgba(255,200,0,0.06)",
              border: "1px dashed rgba(255,200,0,0.3)",
              borderRadius: 14, padding: "12px 14px", marginBottom: 14,
            }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,200,0,0.6)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>
                {tr("profil.sandboxLabel")}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={onTestLevelUp}
                  style={{
                    flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                    background: "rgba(45,227,107,0.15)", color: "#2DE36B",
                    fontWeight: 800, fontSize: 12, cursor: "pointer",
                  }}
                >
                  {tr("profil.testLevelUp")}
                </button>
                <button
                  onClick={onTestBetResult}
                  style={{
                    flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                    background: "rgba(59,130,246,0.15)", color: "#60A5FA",
                    fontWeight: 800, fontSize: 12, cursor: "pointer",
                  }}
                >
                  {tr("profil.testBetResult")}
                </button>
                <button
                  onClick={() => onOpenDuel?.()}
                  style={{
                    flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
                    background: "rgba(168,85,247,0.15)", color: "#c084fc",
                    fontWeight: 800, fontSize: 12, cursor: "pointer",
                  }}
                >
                  🎮 Duel
                </button>
              </div>
            </div>
          )}

          {recent.length > 0 && (
            <>
              <SH title={tr("profil.recentPicksTitle")} />
              {recent.map(({ m, pick, res }) => {
                const won = res.o === pick;
                return (
                  <div
                    key={m.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "9px 11px",
                      marginBottom: 7,
                      background: C.surface,
                      border: `1px solid ${won ? C.turf + "44" : C.line}`,
                      borderRadius: 11,
                      fontSize: 12.5,
                    }}
                  >
                    <Fl team={m.t1} size={16} />
                    <span style={{ color: C.muted }}>vs</span>
                    <Fl team={m.t2} size={16} />
                    <span style={{ flex: 1, color: C.muted, fontSize: 11.5 }}>
                      {pick === "team1"
                        ? m.t1
                        : pick === "team2"
                        ? m.t2
                        : tr("profil.drawPick")}
                    </span>
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontWeight: 900,
                        color: won ? C.turf : "#e06a8c",
                      }}
                    >
                      {won ? `+${pickPts(m, pick)}` : "0"}
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      {selectedItem && (
        <ItemModal
          it={selectedItem}
          isEquipped={isEquipped}
          equippedItem={isEquipped ? null : (equippedBySlot[selectedItem.slot] ?? null)}
          onEquip={onEquip}
          onUnequip={onUnequip}
          onClose={() => setSelectedItem(null)}
          onSell={onSell}
          onStore={onStore}
        />
      )}

      {redesignOpen && (
        <ItemRedesignModal
          me={me}
          onConfirm={handleRedesign}
          onClose={() => setRedesignOpen(false)}
        />
      )}

      {coffreOpen && (
        <div
          onClick={() => setCoffreOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9998,
            background: "rgba(0,0,0,0.65)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto",
              background: "#181825", borderTopLeftRadius: 20, borderTopRightRadius: 20,
              border: "1px solid rgba(249,226,175,0.35)", borderBottom: "none",
              padding: 20, color: "#fff",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <h2 style={{ fontWeight: 900, fontSize: 18, margin: 0 }}>📦 Vault</h2>
              <span style={{ fontSize: 12, fontWeight: 800, color: "#f9e2af" }}>
                {(me.coffre_items?.length ?? 0)} / {coffreCapacity(me.coffre_count)}
              </span>
            </div>

            {/* Barre d'onglets 1..MAX — possédés = normal, prochain = achetable, reste = verrouillé */}
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {Array.from({ length: MAX_COFFRE_CHESTS }).map((_, i) => {
                const owned = i < (me.coffre_count ?? 1);
                const isNext = i === (me.coffre_count ?? 1);
                const selectable = owned || isNext;
                const active = vaultTab === i;
                return (
                  <button
                    key={i}
                    onClick={selectable ? () => { setVaultTab(i); setConfirmExtend(false); } : undefined}
                    disabled={!selectable}
                    style={{
                      flex: 1, padding: "8px 0", borderRadius: 10,
                      fontWeight: 800, fontSize: 13,
                      border: active ? "1px solid #f9e2af" : "1px solid rgba(255,255,255,0.12)",
                      background: active ? "rgba(249,226,175,0.18)" : "rgba(255,255,255,0.05)",
                      color: owned ? "#fff" : selectable ? "#f9e2af" : "rgba(255,255,255,0.25)",
                      cursor: selectable ? "pointer" : "not-allowed",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
                    }}
                  >
                    {owned ? i + 1 : <>🔒 {i + 1}</>}
                  </button>
                );
              })}
            </div>

            {vaultTab < (me.coffre_count ?? 1) ? (
              /* Onglet possédé : 20 cases (item = tap pour ressortir, sinon case vide) */
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
                {Array.from({ length: COFFRE_SLOTS_PER_CHEST }).map((_, c) => {
                  const it = (me.coffre_items ?? [])[vaultTab * COFFRE_SLOTS_PER_CHEST + c];
                  return it ? (
                    <ItemToken key={it.iid} it={it} onClick={() => onRetrieve?.(it)} />
                  ) : (
                    <div
                      key={c}
                      style={{
                        aspectRatio: "1", borderRadius: 11,
                        border: "1.5px dashed rgba(255,255,255,0.18)",
                        background: "rgba(255,255,255,0.03)",
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              /* Onglet verrouillé (prochain achetable) : cases fantômes + overlay centré */
              <div style={{ position: "relative" }}>
                {/* Cases vides fantômes — même grille que l'onglet possédé */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, opacity: 0.4 }}>
                  {Array.from({ length: COFFRE_SLOTS_PER_CHEST }).map((_, c) => (
                    <div
                      key={c}
                      style={{
                        aspectRatio: "1", borderRadius: 11,
                        border: "1.5px dashed rgba(255,255,255,0.18)",
                        background: "rgba(255,255,255,0.03)",
                      }}
                    />
                  ))}
                </div>
                {/* Overlay centré */}
                <div style={{
                  position: "absolute", inset: 0,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 10,
                }}>
                  <img src={TICKET_ICON} alt="" style={{ width: 54, height: 54, objectFit: "contain" }} />
                  {!confirmExtend && (
                    <>
                      <p style={{ fontSize: 13, color: C.muted, margin: 0, textAlign: "center" }}>
                        Unlock vault chest {vaultTab + 1} · {COFFRE_SLOTS_PER_CHEST} extra slots
                      </p>
                      <button
                        onClick={() => setConfirmExtend(true)}
                        style={{
                          padding: "11px 22px", borderRadius: 12, border: "none",
                          background: "#f9e2af", color: "#1e1e2e", fontWeight: 800, fontSize: 14,
                          cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8,
                        }}
                      >
                        Extend {COFFRE_CHEST_COST_TICKETS}
                        <img src={TICKET_ICON} alt="" style={{ width: 18, height: 18, objectFit: "contain" }} />
                      </button>
                    </>
                  )}
                  {confirmExtend && (
                    <>
                      <p style={{ fontSize: 14, fontWeight: 700, margin: "0 0 4px", textAlign: "center" }}>
                        Pay {COFFRE_CHEST_COST_TICKETS} tickets to unlock this chest?
                      </p>
                      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 14px", textAlign: "center" }}>
                        You have {me.tickets ?? 0} 🎟️
                      </p>
                      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                        <button
                          onClick={() => setConfirmExtend(false)}
                          style={{
                            flex: 1, maxWidth: 140, padding: "11px", borderRadius: 12,
                            border: "1px solid rgba(255,255,255,0.15)", background: "transparent",
                            color: "#cdd6f4", fontWeight: 700, fontSize: 14, cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => { onBuyCoffre?.(); setConfirmExtend(false); }}
                          disabled={(me.tickets ?? 0) < COFFRE_CHEST_COST_TICKETS}
                          style={{
                            flex: 1, maxWidth: 140, padding: "11px", borderRadius: 12, border: "none",
                            background: (me.tickets ?? 0) >= COFFRE_CHEST_COST_TICKETS ? "#f9e2af" : "rgba(255,255,255,0.1)",
                            color: (me.tickets ?? 0) >= COFFRE_CHEST_COST_TICKETS ? "#1e1e2e" : "rgba(255,255,255,0.35)",
                            fontWeight: 800, fontSize: 14,
                            cursor: (me.tickets ?? 0) >= COFFRE_CHEST_COST_TICKETS ? "pointer" : "not-allowed",
                          }}
                        >
                          Confirm
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={() => setCoffreOpen(false)}
              style={{
                width: "100%", marginTop: 16, padding: "11px", borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.15)", background: "transparent",
                color: "#cdd6f4", fontWeight: 700, fontSize: 14, cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
